/**
 * Indices API
 * Endpoints for managing search indices (BM25, vector, etc.)
 */

import { getAPIClient } from './client';

export interface BM25IndexStatus {
  project_id: string;
  /** Number of indexed text entries (chunk-level rows in IndexEntry, not artifact count). */
  total_documents: number;
  /** Alias for total_documents — BM25 indexes chunk text, not whole artifacts. */
  total_chunks?: number;
  index_size_mb: number;
  last_built_at: string | null;
  build_status: 'not_built' | 'building' | 'complete' | 'queued';
}

export interface BM25BuildRequest {
  project_id: string;
  async_build?: boolean;
}

export interface BM25BuildResponse {
  status: 'triggered' | 'already_running' | 'queued' | 'completed' | 'skipped' | 'error';
  task_id?: string;
  job_id?: string;
  message: string;
  entry_count?: number;
  project_id: string;
  project_name?: string;
  info?: string;
}

/**
 * Get BM25 index status for a project
 */
export async function getBM25Status(projectId: string): Promise<BM25IndexStatus> {
  const client = getAPIClient();
  return client.get(`/indices/bm25/status/${projectId}`);
}

/**
 * Build BM25 index for a project from existing indexed documents
 * 
 * This does NOT re-embed documents - it builds BM25 keyword index from
 * existing text content in IndexEntry table.
 * 
 * @param projectId - Project UUID
 * @param asyncBuild - If true (default), runs as background job
 */
export async function buildBM25Index(
  projectId: string,
  asyncBuild: boolean = true
): Promise<BM25BuildResponse> {
  const client = getAPIClient();
  // Correct endpoint: /indices/bm25/{project_id}/build (project_id is path parameter, not body)
  return client.post<BM25BuildResponse>(`/indices/bm25/${projectId}/build`);
}


